import { Alert, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type {
    OpticalAddressField,
    OpticalAddressReview,
} from '../domain/opticalAddressProposal';

import { DocumentCalibrationPanel } from './DocumentCalibrationPanel';

/**
 * The address the document carried, in whatever state the reader left it.
 *
 * **Why it is always on screen.** It used to appear only when there was something to offer, so a
 * scan that read no address showed the operator the identity fields and then nothing at all - which
 * reads as "this build has no address feature", not as the specific and usually answerable thing
 * that actually happened. On 2026-09-11 that is exactly how a calibration prompt looked: the
 * measurement had been taken, the operator was the only one who could act on it, and the screen
 * said nothing. A section that is always present cannot hide a state by omitting itself.
 *
 * **It proposes and nothing more.** There is no write path through this component. Every value
 * arrives unticked, no confidence can tick one, and the operator's ticks are the only thing the
 * modal's apply step reads. Accepting the identity fields does not accept an address.
 */
export const DocumentAddressSection = ({
    review,
    previews,
    children,
}: {
    review: OpticalAddressReview;
    /**
     * The captures to draw measured geometry over, by illumination.
     *
     * Both, because both are measured. A panel that could only show the visible frame could only
     * ever ask half the question a calibration run exists to put to an operator.
     */
    previews: { readonly visible: string | null; readonly infrared: string | null };
    /** The per-component rows, rendered by the modal that owns the acceptance state. */
    children?: React.ReactNode;
}) => {
    const { t } = useTranslation();

    return (
        <div data-testid="document-address-section">
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
                {t('documentScan.address.title')}
            </Typography.Title>

            {review.state === 'calibrationRequired' && (
                <Alert
                    type="info"
                    showIcon
                    message={t('documentScan.address.calibrationRequired')}
                    description={t('documentScan.address.calibrationRequiredDetail')}
                />
            )}

            {review.state === 'visibleImageUnavailable' && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('documentScan.address.visibleImageUnavailable')}
                    description={t('documentScan.address.visibleImageUnavailableDetail')}
                />
            )}

            {/* Configuration rather than capture: the template names an illumination this scan did
                not supply, or names none at all. Shown apart from the state above because the fix
                is a template or a reader setting, not another scan. */}
            {review.state === 'sourceUnavailable' && (
                <Alert
                    type="error"
                    showIcon
                    data-testid="address-source-unavailable"
                    message={t('documentScan.address.sourceUnavailable')}
                    description={t(`documentScan.addressReason.${review.reasonCode}`, {
                        defaultValue: t('documentScan.address.sourceUnavailableDetail'),
                    })}
                />
            )}

            {review.state === 'notExtracted' && (
                <Alert
                    type="info"
                    showIcon
                    message={t('documentScan.address.notExtracted')}
                    description={t(`documentScan.addressReason.${review.reasonCode}`, {
                        defaultValue: t('documentScan.addressReason.default'),
                    })}
                />
            )}

            {review.state === 'unsupported' && (
                <Alert
                    type="info"
                    showIcon
                    message={t('documentScan.address.unsupported')}
                    description={t('documentScan.address.unsupportedDetail')}
                />
            )}

            {/* A current service that produced no address result. An error rather than a notice,
                because unlike every other state here nothing about the card or the operator's
                handling of it caused this, and nothing they do will change it. It used to be shown
                as "a service installed before this feature", which sent people looking for an
                upgrade they already had. */}
            {review.state === 'contractUnavailable' && (
                <Alert
                    type="error"
                    showIcon
                    data-testid="address-contract-unavailable"
                    message={t('documentScan.address.contractUnavailable')}
                    description={t('documentScan.address.contractUnavailableDetail')}
                />
            )}

            {review.state === 'proposed' && (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* Stated once, above every row, and repeated on each row's tags. The operator
                        is being handed values with no reliability score behind them, and that is
                        the single most important thing about them. */}
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            review.confidenceAvailable
                                ? t('documentScan.addressUnverified')
                                : t('documentScan.confidenceUnavailableManual')
                        }
                        description={t('documentScan.addressUnverifiedDetail')}
                    />

                    {children}

                    {review.missing.length > 0 && <MissingComponents missing={review.missing} />}
                </Space>
            )}

            {/* Inside the section, because the geometry *is* the address answer in this state - the
                operator is being asked where the address is, not shown an unrelated tool.

                Rendered from the measured collection, each entry already structurally checked and
                each naming its own illumination. The guard used to read `calibration !== undefined`,
                which is true for the `null` the service sends on every scan that measured no
                geometry - so the panel rendered, read `.bands` off null, and on 2026-09-14 took the
                entire application down with it, twice, while an operator held a card against the
                reader. An empty collection renders no panel, which is the honest answer. */}
            {review.calibrations.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <DocumentCalibrationPanel
                        calibrations={review.calibrations}
                        previews={previews}
                    />
                </div>
            )}

        </div>
    );
};

/**
 * What this scan did not read, named rather than left as an absence.
 *
 * A partial read has to look partial. Listing the components that came back and stopping there
 * would let a missing street read as a card that has no street, and the two call for completely
 * different actions from the person typing the rest of the form.
 */
const MissingComponents = ({ missing }: { missing: readonly OpticalAddressField[] }) => {
    const { t } = useTranslation();

    return (
        <Alert
            type="info"
            showIcon
            data-testid="address-missing-components"
            message={t('documentScan.address.partial')}
            description={t('documentScan.address.partialDetail', {
                fields: missing.map(field => t(`documentScan.addressField.${field}`)).join(', '),
            })}
        />
    );
};
